/* eslint-disable react/display-name */
import React from 'react';
import styled from '@emotion/styled';

export const BaseButtonStyle = {
  fontFamily: 'Inter, sans-serif !important',
  rounded: 'md',
  lineHeight: 1,
  fontWeight: 'medium',
  boxShadow: 'none',
};

const BasePrimaryStyle = {
  ...BaseButtonStyle,
  size: 'md',
  colorScheme: 'blue',
  variant: 'solid',
};

const BaseSecondaryStyle = {
  ...BaseButtonStyle,
  size: 'md',
  border: 'none',
  colorScheme: 'blue',
  variant: 'subtle',
  _disabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
    _hover: {
      bg: 'blue.100',
    },
  },
};

const OutlineButtonBase = {
  ...BaseButtonStyle,
  bg: 'none',
  size: 'md',
  color: 'gray.800',
  border: '1px solid',
  borderColor: 'gray.700',
  fontWeight: 'semibold',
  rounded: 'md',
  _hover: {
    bg: 'blue.50',
  },
  _focus: {
    bg: 'blue.100',
  },
  _active: {
    bg: 'blue.100',
  },
};
export const Buttons = {
  Outline: {
    ...OutlineButtonBase,
  },
  Primary: {
    ...BasePrimaryStyle,
  },
  PrimarySmall: {
    ...BasePrimaryStyle,
    size: 'sm',
  },
  Secondary: {
    ...BaseSecondaryStyle,
  },
  SecondarySmall: {
    ...BaseSecondaryStyle,
    px: 3,
    rounded: '5px',
    height: '32px',
    fontSize: 'md',
  },
  Ghost: {
    ...BaseButtonStyle,
    px: 4,
    variant: 'ghost',
    colorScheme: 'blue',
    fontWeight: 'medium',
    size: 'sm',
    h: 10,
  },
};

export const Button = styled.button<{ background?: string; color?: string }>`
  position: relative;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  background: ${(props) => props.background || '#CCC'};
  border: none;
  display: inline-block;
  height: 32px;
  line-height: 32px;
  padding: 0 12px;
  border-radius: 3px;
  color: ${(props) => props.color || '#666'};
  text-decoration: none;
  vertical-align: top;
  &[disabled] {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ButtonLoading = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
`;

const SpinLoading = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  width: 20px;
  height: 22px;
`;

const ButtonContent = styled.div<{ isLoading?: boolean }>`
  color: ${(props) => props.isLoading && 'transparent'};
`;

export default (({ children, loading, disabled, ...props }) => {
  return (
    <Button {...props} disabled={loading || disabled}>
      <ButtonContent isLoading={loading}>{children}</ButtonContent>
      {loading && (
        <ButtonLoading>
          <SpinLoading>
            <svg
              fill="#fff"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 32 32"
            >
              <path
                opacity=".25"
                d="M16 0 A16 16 0 0 0 16 32 A16 16 0 0 0 16 0 M16 4 A12 12 0 0 1 16 28 A12 12 0 0 1 16 4"
              />
              <path
                d="M16 0 A16 16 0 0 1 32 16 L28 16 A12 12 0 0 0 16 4z"
                transform="rotate(218.551 16 16)"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 16 16"
                  to="360 16 16"
                  dur="0.8s"
                  repeatCount="indefinite"
                />
              </path>
            </svg>
          </SpinLoading>
        </ButtonLoading>
      )}
    </Button>
  );
}) as React.FC<{
  loading: boolean;
  disabled: boolean;
  props: unknown;
}>;
